/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import {
  CUSTOMER_STATUS_LEVELS,
  Order,
  Reservation,
  RestaurantCustomer,
} from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useAuth } from "@app/vue/providers/Auth";
import {
  formatCurrency,
  getInitials,
  getResTime,
  getTotalSpent,
  hashColor,
  isVIP as checkIsVIP,
} from "../utils";
import { ActiveSection } from "./CustomerCRMDetail/constants";
import {
  getBirthdayLabel,
  getCustomerId,
  normalizeBirthDayMonth,
  normalizeBirthYear,
} from "./CustomerCRMDetail/customerDetailHelpers";
import { CustomerCRMEditForm } from "./CustomerCRMDetail/CustomerCRMEditForm";
import { CustomerCRMHeader } from "./CustomerCRMDetail/CustomerCRMHeader";
import { CustomerCRMOrdersSection } from "./CustomerCRMDetail/CustomerCRMOrdersSection";
import { CustomerCRMOverview } from "./CustomerCRMDetail/CustomerCRMOverview";
import { CustomerCRMReservationsSection } from "./CustomerCRMDetail/CustomerCRMReservationsSection";
import { CustomerCRMTabs } from "./CustomerCRMDetail/CustomerCRMTabs";

export const CustomerCRMDetail = defineComponent<{
  customer: RestaurantCustomer;
  onClose: () => void;
  onUpdated: (customer: RestaurantCustomer) => void;
  hideCloseButton?: boolean;
  hideName?: boolean;
}>({
  name: "CustomerCRMDetail",
  props: ["customer", "onClose", "onUpdated", "hideCloseButton", "hideName"],
  setup(props) {
    const trpc = useTrpc();
    const auth = useAuth();
    const { t } = useLocalization();

    const activeSection = ref<ActiveSection>("overview");
    const isEditing = ref(false);
    const isSaving = ref(false);
    const isTogglingVIP = ref(false);
    const orders = ref<Order[]>([]);
    const reservations = ref<Reservation[]>([]);
    const reviewRequests = ref<any[]>([]);
    const loadingOrders = ref(false);
    const loadingReservations = ref(false);
    const ordersLoaded = ref(false);
    const reservationsLoaded = ref(false);
    let detailToken = 0;

    const editName = ref("");
    const editEmail = ref("");
    const editPhone = ref("");
    const editBirthDayMonth = ref("");
    const editBirthYear = ref("");
    const editStatusLevel = ref("");
    const editTags = ref<string[]>([]);
    const newTag = ref("");
    const saveError = ref<string | null>(null);

    const isVIP = computed(() => checkIsVIP(props.customer));
    const totalSpent = computed(() => getTotalSpent(props.customer));
    const initials = computed(() =>
      getInitials(props.customer.name, props.customer.email),
    );
    const avatarColor = computed(() =>
      hashColor(
        props.customer.email || props.customer.name || props.customer.id,
      ),
    );
    const cleanTags = computed(
      () => props.customer.restaurant_tags || props.customer.tags || [],
    );
    const birthdayLabel = computed(() => getBirthdayLabel(props.customer));

    const syncEditFields = () => {
      editName.value = props.customer.name || "";
      editEmail.value = props.customer.email || "";
      editPhone.value = props.customer.phone || "";
      editBirthDayMonth.value = props.customer.birth_day_month || "";
      editBirthYear.value = props.customer.birth_year
        ? String(props.customer.birth_year)
        : "";
      editStatusLevel.value = props.customer.status_level || "";
      editTags.value = [...cleanTags.value];
      newTag.value = "";
    };

    const upcomingReservations = computed(() => {
      const now = Date.now();
      return reservations.value
        .filter((r: any) => {
          const rDate = getResTime(r);
          if (!rDate) return false;
          const status = r.status || "";
          return (
            rDate >= now - 86400000 &&
            ["confirmed", "pending", "arrived"].includes(status)
          );
        })
        .sort((a: any, b: any) => getResTime(a) - getResTime(b))
        .slice(0, 3);
    });

    const pastReservations = computed(() => {
      const now = Date.now();
      return reservations.value
        .filter((r: any) => {
          const rDate = getResTime(r);
          if (!rDate) return false;
          return (
            rDate < now ||
            ["completed", "no-show", "cancelled", "cancelled_by_user"].includes(
              r.status || "",
            )
          );
        })
        .sort((a: any, b: any) => getResTime(b) - getResTime(a));
    });

    const recentOrders = computed(() =>
      [...orders.value]
        .sort((a: any, b: any) => {
          const da = new Date(
            a.created_timestamp || a.created_at || 0,
          ).getTime();
          const db = new Date(
            b.created_timestamp || b.created_at || 0,
          ).getTime();
          return db - da;
        })
        .slice(0, 5),
    );

    const favouriteItems = computed(() => {
      const itemCounts: Record<
        string,
        { name: string; count: number; totalSpent: number }
      > = {};
      for (const order of orders.value) {
        const items = (order as any).items;
        if (!Array.isArray(items)) continue;
        for (const item of items) {
          const name =
            item.product?.name ||
            item.product_name ||
            t("reservations.customers.product_number", [item.product_id]);
          const key = String(item.product_id);
          if (!itemCounts[key])
            itemCounts[key] = { name, count: 0, totalSpent: 0 };
          itemCounts[key].count += item.quantity || 1;
          itemCounts[key].totalSpent +=
            (item.price || 0) * (item.quantity || 1);
        }
      }
      return Object.values(itemCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    });

    const fetchReviewRequests = async () => {
      const token = detailToken;
      try {
        const result = await trpc.reservationCustomers.reviewRequests.query({
          id: getCustomerId(props.customer),
        });
        if (token === detailToken) reviewRequests.value = result;
      } catch {
        if (token === detailToken) reviewRequests.value = [];
      }
    };

    const fetchOrders = async () => {
      if (ordersLoaded.value) return;
      const token = detailToken;
      loadingOrders.value = true;
      try {
        const result = await trpc.reservationCustomers.orders.query({
          id: getCustomerId(props.customer),
        });
        if (token !== detailToken) return;
        orders.value = result as Order[];
        ordersLoaded.value = true;
      } catch {
        if (token === detailToken) orders.value = [];
      } finally {
        if (token === detailToken) loadingOrders.value = false;
      }
    };

    const fetchReservations = async () => {
      if (reservationsLoaded.value) return;
      const token = detailToken;
      loadingReservations.value = true;
      try {
        const result = await trpc.reservationCustomers.reservations.query({
          id: getCustomerId(props.customer),
        });
        if (token !== detailToken) return;
        reservations.value = result as Reservation[];
        reservationsLoaded.value = true;
      } catch {
        if (token === detailToken) reservations.value = [];
      } finally {
        if (token === detailToken) loadingReservations.value = false;
      }
    };

    const loadAllData = async () => {
      detailToken++;
      await Promise.all([fetchOrders(), fetchReservations()]);
      fetchReviewRequests();
    };

    const toggleVIP = async () => {
      if (isTogglingVIP.value) return;
      isTogglingVIP.value = true;
      const newStatus = isVIP.value
        ? CUSTOMER_STATUS_LEVELS.REGULAR
        : CUSTOMER_STATUS_LEVELS.VIP;
      try {
        const updated = await trpc.reservationCustomers.update.mutate({
          id: getCustomerId(props.customer),
          status_level: newStatus,
        });
        props.onUpdated(updated);
      } catch (error) {
        console.error("Failed to toggle VIP:", error);
        saveError.value = t("reservations.customers.failed_update_vip");
        setTimeout(() => (saveError.value = null), 3000);
      } finally {
        isTogglingVIP.value = false;
      }
    };

    const saveChanges = async () => {
      isSaving.value = true;
      try {
        const birthDayMonth = editBirthDayMonth.value.trim()
          ? normalizeBirthDayMonth(editBirthDayMonth.value)
          : null;
        const birthYear = editBirthYear.value.trim()
          ? normalizeBirthYear(editBirthYear.value)
          : null;

        const updated = await trpc.reservationCustomers.update.mutate({
          id: getCustomerId(props.customer),
          name: editName.value || undefined,
          email: editEmail.value || undefined,
          phone: editPhone.value || undefined,
          status_level: editStatusLevel.value || undefined,
          birth_day_month: birthDayMonth,
          birth_year: birthYear,
          tags: [...editTags.value],
        });
        props.onUpdated(updated);
        isEditing.value = false;
      } catch (error) {
        console.error("Failed to save customer:", error);
        saveError.value = t("reservations.customers.failed_save_changes");
        setTimeout(() => (saveError.value = null), 3000);
      } finally {
        isSaving.value = false;
      }
    };

    const addTag = () => {
      const tag = newTag.value.trim().toLowerCase().replace(/\s+/g, "_");
      if (tag && !editTags.value.includes(tag)) editTags.value.push(tag);
      newTag.value = "";
    };

    const removeTag = (tag: string) => {
      editTags.value = editTags.value.filter((t) => t !== tag);
    };

    const startEditing = () => {
      syncEditFields();
      isEditing.value = true;
    };

    const cancelEditing = () => {
      isEditing.value = false;
      syncEditFields();
    };

    const statCards = computed(() => [
      {
        label: t("reservations.customers.total_spent"),
        value: formatCurrency(
          totalSpent.value,
          auth.user.value?.restaurant.currency || "CHF",
        ),
        icon: "payments",
        color: "text-success",
        bg: "bg-success/8",
        border: "border-success/12",
      },
      {
        label: t("reservations.customers.orders"),
        value: String(props.customer.total_orders ?? 0),
        icon: "receipt_long",
        color: "text-info",
        bg: "bg-info/8",
        border: "border-info/12",
      },
      {
        label: t("reservations.customers.reservations"),
        value: String(props.customer.total_reservations ?? 0),
        icon: "event_seat",
        color: "text-warning",
        bg: "bg-warning/8",
        border: "border-warning/12",
      },
      {
        label: t("reservations.customers.no_shows"),
        value: String(props.customer.total_reservations_noshows ?? 0),
        icon: "person_off",
        color:
          (props.customer.total_reservations_noshows ?? 0) > 0
            ? "text-error"
            : "text-base-content/30",
        bg:
          (props.customer.total_reservations_noshows ?? 0) > 0
            ? "bg-error/8"
            : "bg-base-content/3",
        border:
          (props.customer.total_reservations_noshows ?? 0) > 0
            ? "border-error/12"
            : "border-base-content/5",
      },
    ]);

    watch(
      () => getCustomerId(props.customer),
      () => {
        activeSection.value = "overview";
        orders.value = [];
        reservations.value = [];
        reviewRequests.value = [];
        ordersLoaded.value = false;
        reservationsLoaded.value = false;
        isEditing.value = false;
        syncEditFields();
        loadAllData();
      },
    );

    onMounted(() => {
      syncEditFields();
      loadAllData();
    });

    return () => (
      <div class="flex flex-col h-full overflow-hidden">
        <CustomerCRMHeader
          customer={props.customer}
          avatarColor={avatarColor.value}
          initials={initials.value}
          birthdayLabel={birthdayLabel.value}
          isVIP={isVIP.value}
          isEditing={isEditing.value}
          isTogglingVIP={isTogglingVIP.value}
          hideCloseButton={props.hideCloseButton}
          onToggleVIP={toggleVIP}
          onStartEditing={startEditing}
          onClose={props.onClose}
          hideName={props.hideName}
        />

        <CustomerCRMTabs
          activeSection={activeSection.value}
          ordersCount={orders.value.length}
          fallbackOrdersCount={props.customer.total_orders ?? 0}
          reservationsCount={reservations.value.length}
          fallbackReservationsCount={props.customer.total_reservations ?? 0}
          ordersLoaded={ordersLoaded.value}
          reservationsLoaded={reservationsLoaded.value}
          loadingOrders={loadingOrders.value}
          loadingReservations={loadingReservations.value}
          onChange={(section) => (activeSection.value = section)}
        />

        {saveError.value && (
          <div class="mx-4 mt-3 alert alert-error py-2 text-xs">
            <span>{saveError.value}</span>
          </div>
        )}

        <div class="flex-1 overflow-y-auto p-4 md:p-5">
          {activeSection.value === "overview" &&
            (isEditing.value ? (
              <CustomerCRMEditForm
                nameValue={editName.value}
                emailValue={editEmail.value}
                phoneValue={editPhone.value}
                birthDayMonthValue={editBirthDayMonth.value}
                birthYearValue={editBirthYear.value}
                statusLevelValue={editStatusLevel.value}
                tags={editTags.value}
                newTagValue={newTag.value}
                isSaving={isSaving.value}
                onNameInput={(value) => (editName.value = value)}
                onEmailInput={(value) => (editEmail.value = value)}
                onPhoneInput={(value) => (editPhone.value = value)}
                onBirthDayMonthInput={(value) =>
                  (editBirthDayMonth.value = value)
                }
                onBirthYearInput={(value) => (editBirthYear.value = value)}
                onStatusLevelInput={(value) => (editStatusLevel.value = value)}
                onNewTagInput={(value) => (newTag.value = value)}
                onAddTag={addTag}
                onRemoveTag={removeTag}
                onCancel={cancelEditing}
                onSave={saveChanges}
              />
            ) : (
              <CustomerCRMOverview
                customer={props.customer}
                tags={cleanTags.value}
                statCards={statCards.value}
                upcomingReservations={upcomingReservations.value}
                recentOrders={recentOrders.value}
                favouriteItems={favouriteItems.value}
                reviewRequests={reviewRequests.value}
                loadingOrders={loadingOrders.value}
                loadingReservations={loadingReservations.value}
                allOrdersCount={orders.value.length}
                allReservationsCount={reservations.value.length}
                onStartEditing={startEditing}
                onSectionChange={(section) => (activeSection.value = section)}
              />
            ))}

          {activeSection.value === "orders" && (
            <CustomerCRMOrdersSection
              orders={orders.value}
              loading={loadingOrders.value}
            />
          )}

          {activeSection.value === "reservations" && (
            <CustomerCRMReservationsSection
              customer={props.customer}
              reservations={reservations.value}
              upcomingReservations={upcomingReservations.value}
              pastReservations={pastReservations.value}
              loading={loadingReservations.value}
            />
          )}
        </div>
      </div>
    );
  },
});
